import React from 'react';
import { ChevronRight, ExternalLink, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Highlighter } from '../../lib/paper';

const ROW = 'flex min-h-[52px] w-full items-center gap-3 border-none bg-transparent p-0 text-left no-underline';

/** Settings row (mobile's SettingRow): highlighter icon chip, label, optional value and chevron. */
export const SettingRow: React.FC<{
  icon: LucideIcon;
  tone: Highlighter;
  label: string;
  value?: string;
  onClick?: () => void;
  to?: string;
  href?: string;
  disabled?: boolean;
}> = ({ icon: Icon, tone, label, value, onClick, to, href, disabled }) => {
  const body = (
    <>
      <span
        className="flex h-[30px] w-[30px] shrink-0 -rotate-3 items-center justify-center rounded-xl"
        style={{ backgroundColor: tone.fill, color: tone.ink }}
      >
        <Icon size={15} />
      </span>
      <span className="flex-1 font-body text-[0.9375rem] font-medium text-on-surface">{label}</span>
      {value && <span className="max-w-[48%] truncate font-body text-[0.8125rem] text-on-surface-variant">{value}</span>}
      {href ? (
        <ExternalLink size={15} className="text-on-surface-variant" />
      ) : (onClick || to) && (
        <ChevronRight size={18} className="text-on-surface-variant" />
      )}
    </>
  );
  const interactive = 'cursor-pointer transition-opacity hover:opacity-70 disabled:cursor-default disabled:opacity-50';
  if (to) return <Link to={to} className={`${ROW} ${interactive}`}>{body}</Link>;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={`${ROW} ${interactive}`}>{body}</a>;
  if (onClick) return <button type="button" onClick={onClick} disabled={disabled} className={`${ROW} ${interactive}`}>{body}</button>;
  return <div className={ROW}>{body}</div>;
};

/** Group of rows on one card, with dividers inset past the chip like ruled lines. */
export const SettingGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <div className="mt-3 rounded-xl border border-solid border-outline-variant bg-surface-container-lowest px-3.5">
      {rows.map((row, i) => (
        <div key={i}>
          {i > 0 && <div className="ml-[42px] h-px bg-outline-variant" />}
          {row}
        </div>
      ))}
    </div>
  );
};
